import React, { useCallback, useEffect, useRef, useState } from "react";
import {
  Button,
  Col,
  FormFeedback,
  FormGroup,
  Input,
  Label,
  Modal,
  ModalBody,
  ModalFooter,
  Row,
} from "reactstrap";
import Select from "react-select";
import AsyncSelect from "react-select/async";
import api from "services/api";

// Modal de "Frete Combinado": registra uma cotação que foi negociada por fora
// do sistema, sem simulação. Status já entra como Aprovado e origemCotacao =
// "Combinado". Reusa o endpoint POST /embarcadores/embarques.

const parseBR = (value) => {
  if (value === null || value === undefined || value === "") return 0;
  if (typeof value === "number") return Number.isFinite(value) ? value : 0;
  if (typeof value !== "string") return 0;
  const cleaned = value
    .replace(/\s/g, "")
    .replace(/[R$]/gi, "")
    .replace(/[^\d.,-]/g, "");
  if (!cleaned) return 0;
  let normalized = cleaned;
  if (cleaned.includes(",")) {
    normalized = cleaned.replace(/\./g, "").replace(",", ".");
  } else if (cleaned.includes(".")) {
    const parts = cleaned.split(".");
    if (parts.length > 2) {
      const last = parts.pop();
      normalized = `${parts.join("")}.${last}`;
    }
  }
  normalized = normalized.replace(/[^0-9.-]/g, "");
  const parsed = Number(normalized);
  return Number.isNaN(parsed) ? 0 : parsed;
};

const formatBRL = (value) => {
  const number = parseBR(value);
  if (!number) return "";
  return number.toLocaleString("pt-BR", {
    style: "currency",
    currency: "BRL",
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });
};

const buildRemetenteOptions = (items) => {
  if (!Array.isArray(items)) return [];
  const seen = new Set();
  return items.reduce((acc, item) => {
    const value = item?.embarcador_id;
    if (value == null || seen.has(String(value))) return acc;
    seen.add(String(value));
    acc.push({
      value,
      matrizId: item?.matriz_id ?? item?.matrizId ?? "",
      label: item?.embarcador_fantasia,
      cnpj:
        item?.embarcador_cnpj ||
        item?.cnpj ||
        item?.cnpjRemetente ||
        item?.cnpj_remetente ||
        "",
    });
    return acc;
  }, []);
};

const emptyForm = {
  remetente: null,
  uf: "",
  cidade: "",
  idCidadeDestino: null,
  numeroNota: "",
  codigoPedido: "",
  peso: "",
  valorNF: "",
  transportadora: "",
  valorFrete: "",
  prazo: "",
  observacoes: "",
};

const FreteCombinadoModal = ({ open, onClose, empresaId, onSaved, onError }) => {
  const [formData, setFormData] = useState(emptyForm);
  const [errors, setErrors] = useState({});
  const [saving, setSaving] = useState(false);
  const [remetentes, setRemetentes] = useState([]);
  const [estados, setEstados] = useState([]);
  const debounceTimerRef = useRef(null);

  // Carrega remetentes e estados quando o modal abre.
  useEffect(() => {
    if (!open || !empresaId) return;
    let cancelled = false;
    setFormData(emptyForm);
    setErrors({});
    Promise.all([
      api.get(`/embarcadores/${empresaId}`),
      api.get("/frete/estados").catch(() => ({ data: [] })),
    ])
      .then(([remRes, estadosRes]) => {
        if (cancelled) return;
        const remOptions = buildRemetenteOptions(remRes.data);
        setRemetentes(remOptions);
        setEstados(
          Array.isArray(estadosRes.data)
            ? estadosRes.data.map((e) => ({ value: e.uf, label: e.uf, id: e.id }))
            : [],
        );
        if (remOptions.length > 0) {
          setFormData((prev) => ({ ...prev, remetente: remOptions[0] }));
        }
      })
      .catch(() => {});
    return () => {
      cancelled = true;
    };
  }, [open, empresaId]);

  const handleChange = useCallback((field, value) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
    setErrors((prev) => ({ ...prev, [field]: undefined }));
  }, []);

  const loadCidadeOptions = useCallback(
    (inputValue) =>
      new Promise((resolve) => {
        if (debounceTimerRef.current) clearTimeout(debounceTimerRef.current);
        if (!formData.uf) {
          resolve([]);
          return;
        }
        debounceTimerRef.current = setTimeout(async () => {
          try {
            const res = await api.get("/cidades", {
              params: { uf: formData.uf, q: inputValue || "" },
            });
            const options = (res.data || []).map((c) => ({
              value: c.id,
              label: c.nome,
              uf: c.uf,
            }));
            resolve(options);
          } catch {
            resolve([]);
          }
        }, 250);
      }),
    [formData.uf],
  );

  const validate = () => {
    const novosErros = {};
    if (!formData.remetente) novosErros.remetente = "Selecione o remetente";
    if (!formData.uf) novosErros.uf = "Campo obrigatório";
    if (!formData.idCidadeDestino) novosErros.cidade = "Selecione a cidade";
    if (!parseBR(formData.peso)) novosErros.peso = "Informe o peso";
    if (!parseBR(formData.valorNF)) novosErros.valorNF = "Informe o valor da NF";
    if (!String(formData.transportadora).trim()) novosErros.transportadora = "Informe a transportadora";
    if (!parseBR(formData.valorFrete)) novosErros.valorFrete = "Informe o valor do frete";
    setErrors(novosErros);
    return Object.keys(novosErros).length === 0;
  };

  const handleSalvar = async () => {
    if (!validate()) return;
    setSaving(true);
    try {
      const valorNFNumber = parseBR(formData.valorNF);
      const valorFreteNumber = parseBR(formData.valorFrete);
      const pesoNumber = parseBR(formData.peso);
      const prazoNumber = formData.prazo ? Number(String(formData.prazo).replace(/\D/g, "")) : null;
      const embarcadorIdPai =
        formData.remetente?.matrizId ||
        formData.remetente?.value ||
        empresaId;

      const payload = {
        idEmbarcador: embarcadorIdPai,
        dataEmbarque: new Date().toISOString(),
        // PO1-3272: vazio, o backend gera. O BR vai em campo próprio e pode
        // se repetir em vários fretes do mesmo pedido.
        numEmbarque: String(formData.numeroNota).trim() || undefined,
        codigoPedido: String(formData.codigoPedido).trim() || null,
        cliente: "",
        cidadeDestino: formData.cidade,
        ufDestino: formData.uf,
        idCidadeDestino: formData.idCidadeDestino,
        cepDestino: null,
        cnpjRemetente: formData.remetente?.cnpj || "",
        transportadora: String(formData.transportadora).trim(),
        valorFrete: valorFreteNumber,
        // "Aprovado" é só o rótulo da tela: o valor gravado é "Simulado"
        // (statusOptions.js). Gravar o texto "Aprovado" deixava o frete
        // combinado fora dos cards da Minhas Cotações e da Fila de Fretes.
        statusEmbarque: "Simulado",
        origemCotacao: "Combinado",
        pesoBruto: pesoNumber,
        valorNF: valorNFNumber,
        tipoFrete: "CIF",
        observacoes: formData.observacoes || "",
        cotacoes: [
          {
            transportadora: String(formData.transportadora).trim(),
            valor: valorFreteNumber,
          },
        ],
        valor_total: valorFreteNumber,
        prazo: prazoNumber,
        veiculosMix: [],
      };

      const { data } = await api.post("/embarcadores/embarques", payload);
      // PO1-2863: a cotação criada volta com numeroCotacao — vai no toast.
      onSaved && onSaved(data || null);
      onClose && onClose();
    } catch (err) {
      const resp = err.response && err.response.data;
      let msg;
      if (resp && typeof resp.error === "string") msg = resp.error;
      else if (resp && resp.error && typeof resp.error === "object") {
        msg = Object.values(resp.error).filter(Boolean).join(" · ");
      } else msg = "Falha ao salvar frete combinado.";
      onError && onError(msg);
    } finally {
      setSaving(false);
    }
  };

  const ufValue = formData.uf
    ? estados.find((e) => e.value === formData.uf) || {
        value: formData.uf,
        label: formData.uf,
      }
    : null;
  const cidadeValue = formData.idCidadeDestino
    ? { value: formData.idCidadeDestino, label: formData.cidade, uf: formData.uf }
    : null;
  const remetenteValue =
    remetentes.find(
      (r) => String(r.value) === String(formData.remetente?.value),
    ) || formData.remetente || null;

  return (
    <Modal
      isOpen={!!open}
      toggle={onClose}
      size="lg"
      modalClassName="modal-align-top"
      contentClassName="lc-edit-modal"
      fade={false}
    >
      <div
        style={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          background: "var(--everlog-card-hero-bg)",
          borderBottom: "1px solid var(--everlog-card-border)",
          padding: "16px 20px",
        }}
      >
        <span style={{ color: "var(--everlog-card-title)", fontWeight: 600, fontSize: 15 }}>
          Registrar Frete Combinado
        </span>
        <button
          onClick={onClose}
          style={{
            background: "none",
            border: "none",
            color: "var(--everlog-card-title)",
            fontSize: 22,
            lineHeight: 1,
            cursor: "pointer",
            padding: "0 4px",
          }}
          aria-label="Fechar"
        >
          ×
        </button>
      </div>

      <ModalBody style={{ maxHeight: "75vh", overflowY: "auto" }}>
        <p style={{ color: "var(--everlog-card-muted)", marginTop: 0, fontSize: 13 }}>
          Registro de cotação fechada por fora do sistema. Salva direto como{" "}
          <strong>Aprovado</strong>.
        </p>

        <FormGroup>
          <Label className="wizard-label">Remetente *</Label>
          {remetentes.length > 1 ? (
            <Select
              className="wizard-select"
              classNamePrefix="wizard-select"
              options={remetentes}
              value={remetenteValue}
              onChange={(opt) => handleChange("remetente", opt)}
              placeholder="Selecione o remetente"
              isClearable={false}
            />
          ) : (
            <Input
              className="wizard-input"
              value={remetentes[0]?.label || "—"}
              readOnly
            />
          )}
          {errors.remetente && (
            <FormFeedback style={{ display: "block" }}>{errors.remetente}</FormFeedback>
          )}
        </FormGroup>

        <Row>
          <Col md="3">
            <FormGroup>
              <Label className="wizard-label">UF *</Label>
              <Select
                className="wizard-select"
                classNamePrefix="wizard-select"
                options={estados}
                value={ufValue}
                onChange={(opt) => {
                  handleChange("uf", opt?.value || "");
                  handleChange("cidade", "");
                  handleChange("idCidadeDestino", null);
                }}
                placeholder="UF"
                isClearable={false}
              />
              {errors.uf && (
                <FormFeedback style={{ display: "block" }}>{errors.uf}</FormFeedback>
              )}
            </FormGroup>
          </Col>
          <Col md="9">
            <FormGroup>
              <Label className="wizard-label">Cidade *</Label>
              <AsyncSelect
                className="wizard-select"
                classNamePrefix="wizard-select"
                cacheOptions
                defaultOptions
                loadOptions={loadCidadeOptions}
                value={cidadeValue}
                onChange={(opt) => {
                  handleChange("idCidadeDestino", opt?.value || null);
                  handleChange("cidade", opt?.label || "");
                  if (opt?.uf) handleChange("uf", opt.uf);
                }}
                placeholder={
                  formData.uf ? "Digite para buscar..." : "Selecione a UF primeiro"
                }
                isClearable
                isDisabled={!formData.uf}
                noOptionsMessage={({ inputValue }) =>
                  inputValue ? "Nenhuma cidade encontrada" : "Digite para buscar"
                }
                loadingMessage={() => "Buscando..."}
              />
              {errors.cidade && (
                <FormFeedback style={{ display: "block" }}>{errors.cidade}</FormFeedback>
              )}
            </FormGroup>
          </Col>
        </Row>

        <Row>
          <Col md="4">
            <FormGroup>
              <Label className="wizard-label">Nº do pedido</Label>
              <Input
                className="wizard-input"
                value={formData.codigoPedido}
                maxLength={20}
                onChange={(e) => handleChange("codigoPedido", e.target.value)}
                placeholder="Ex.: BR26163"
              />
            </FormGroup>
          </Col>
          <Col md="4">
            <FormGroup>
              <Label className="wizard-label">Número do Embarque</Label>
              <Input
                className="wizard-input"
                value={formData.numeroNota}
                onChange={(e) => handleChange("numeroNota", e.target.value)}
                placeholder="Gerado se vazio"
              />
            </FormGroup>
          </Col>
          <Col md="4">
            <FormGroup>
              <Label className="wizard-label">Valor da NF *</Label>
              <Input
                type="text"
                inputMode="decimal"
                className="wizard-input"
                value={formData.valorNF}
                onChange={(e) => handleChange("valorNF", e.target.value)}
                onBlur={() => {
                  const f = formatBRL(formData.valorNF);
                  if (f) handleChange("valorNF", f);
                }}
                placeholder="R$ 0,00"
                invalid={!!errors.valorNF}
              />
              <FormFeedback>{errors.valorNF}</FormFeedback>
            </FormGroup>
          </Col>
        </Row>

        <Row>
          <Col md="6">
            <FormGroup>
              <Label className="wizard-label">Peso Total (kg) *</Label>
              <Input
                type="text"
                inputMode="decimal"
                className="wizard-input"
                value={formData.peso}
                onChange={(e) => handleChange("peso", e.target.value)}
                placeholder="0"
                invalid={!!errors.peso}
              />
              <FormFeedback>{errors.peso}</FormFeedback>
            </FormGroup>
          </Col>
          <Col md="6">
            <FormGroup>
              <Label className="wizard-label">Prazo (dias)</Label>
              <Input
                type="text"
                inputMode="numeric"
                className="wizard-input"
                value={formData.prazo}
                onChange={(e) =>
                  handleChange("prazo", e.target.value.replace(/\D/g, ""))
                }
                placeholder="Opcional"
              />
            </FormGroup>
          </Col>
        </Row>

        <Row>
          <Col md="6">
            <FormGroup>
              <Label className="wizard-label">Transportadora *</Label>
              <Input
                className="wizard-input"
                value={formData.transportadora}
                onChange={(e) => handleChange("transportadora", e.target.value)}
                placeholder="Nome da transportadora"
                invalid={!!errors.transportadora}
              />
              <FormFeedback>{errors.transportadora}</FormFeedback>
            </FormGroup>
          </Col>
          <Col md="6">
            <FormGroup>
              <Label className="wizard-label">Valor do Frete Combinado *</Label>
              <Input
                type="text"
                inputMode="decimal"
                className="wizard-input"
                value={formData.valorFrete}
                onChange={(e) => handleChange("valorFrete", e.target.value)}
                onBlur={() => {
                  const f = formatBRL(formData.valorFrete);
                  if (f) handleChange("valorFrete", f);
                }}
                placeholder="R$ 0,00"
                invalid={!!errors.valorFrete}
              />
              <FormFeedback>{errors.valorFrete}</FormFeedback>
            </FormGroup>
          </Col>
        </Row>

        <FormGroup>
          <Label className="wizard-label">Observações</Label>
          <Input
            type="textarea"
            className="wizard-input"
            value={formData.observacoes}
            onChange={(e) => handleChange("observacoes", e.target.value)}
            placeholder="Informações adicionais..."
            rows={3}
            style={{ resize: "none" }}
          />
        </FormGroup>
      </ModalBody>

      <ModalFooter style={{ borderTop: "1px solid var(--everlog-card-border)", gap: 8, padding: "16px 20px" }}>
        <Button className="lc-btn-cancel" onClick={onClose} disabled={saving}>
          Cancelar
        </Button>
        <Button color="success" onClick={handleSalvar} disabled={saving}>
          {saving ? "Salvando..." : "Registrar Frete Combinado"}
        </Button>
      </ModalFooter>
    </Modal>
  );
};

export default FreteCombinadoModal;
